import { useMemo } from "react";
import type { LabAssetMeta } from "@/api/lab-assets/lab-assets.types";
import { Operators } from "@monolith-forensics/monolith-ui";
import type {
  DropDownItem,
  FilterDefinition,
  FilterOptionsQuery,
  Operator,
} from "@monolith-forensics/monolith-ui";

import {
  CUSTOM_FIELD_CATEGORIES,
  CustomFieldsQuery,
  isCustomFieldApplicableToType,
  resolveCustomFieldFilter,
} from "@/Features/CustomFields";
import { ASSET_TYPE_IDS } from "./labAssets.constants";
import { fetchLabAssetMeta } from "./labAssets.query";
import type { LabAssetSortableColumn, LabAssetType } from "./labAssets.types";
import {
  LAB_ASSET_TYPE_LABELS,
  VALIDATION_OVERDUE_FIELD,
} from "./labAssets.utils";

/**
 * mfui 2.x dropped BeginsWith, but the API's filterResolver natively supports
 * `startsWith` and its label is in the OperatorSchema enum — a custom operator
 * object recovers it. mfui renders unknown operator values with the default
 * single-value editor.
 */
const StartsWith: Operator = { value: "startsWith", label: "starts with" };

type MetaListKey = keyof LabAssetMeta;

type AssetMetaEntry = { assetType: LabAssetType; meta: LabAssetMeta };

const META_ROW_FIELDS: Record<MetaListKey, string> = {
  vendors: "vendor",
  models: "model",
  locations: "location",
  names: "name",
};

const ASSET_TYPES: LabAssetType[] = ["equipment", "software"];

/**
 * One `GET /assets/meta` per requested asset type — the routes strictly
 * require `asset_type`, and the assets table is unscoped, so a dimension asks
 * for exactly the types whose rows it can match and never waits on (or fails
 * with) a fetch it doesn't read. Each goes through fetchLabAssetMeta, so the
 * shared react-query cache dedupes concurrent opens, holds results for its
 * staleTime, and keeps nothing for a failed response so the next dropdown open
 * retries. Opening several filter dropdowns costs two requests total, not two
 * per dimension. (mfui keeps its own per-cacheKey option cache on top; see
 * fetchLabAssetMeta for what each layer clears.)
 */
const loadAssetMeta = (assetTypes: LabAssetType[]): Promise<AssetMetaEntry[]> =>
  Promise.all(
    assetTypes.map(async (assetType) => ({
      assetType,
      meta: await fetchLabAssetMeta(assetType),
    }))
  );

/**
 * Options for one dimension, restricted to the asset types whose rows can
 * actually match it. The scope matters because the meta routes and the filter
 * columns don't line up one-to-one: `GET /assets/meta?asset_type=software`
 * returns `names` built from `license_name` UNION `acquisitions.acq_tool`,
 * while `dataField: "name"` filters `asset_items.name`, which software rows
 * leave null (same split the column defs encode as `assetTypes`). Merging both
 * types there would offer values that always return zero rows — software names
 * are filtered through the separate License Name dimension instead.
 */
const metaOptionsQuery = (
  list: MetaListKey,
  assetTypes: LabAssetType[] = ASSET_TYPES
): FilterOptionsQuery => ({
  cacheKey: JSON.stringify(["lab-assets", "meta", list, assetTypes]),
  loadPage: async () => {
    const entries = await loadAssetMeta(assetTypes);
    const field = META_ROW_FIELDS[list];
    const values = entries
      .flatMap(
        (entry) =>
          (entry.meta[list] ?? []) as Array<Record<string, string | null>>
      )
      .map((row) => row[field])
      .filter((value): value is string => Boolean(value));
    const items: DropDownItem[] = Array.from(new Set(values))
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }));
    return { items, nextPageParam: null };
  },
});

/**
 * mfui types `dataField` as any string, and the API resolver skips an unknown
 * field with a server-side warn and returns the unfiltered set — so a typo
 * would compile and ship a chip that looks applied and does nothing. Narrowing
 * to LabAssetSortableColumn (the mirror of the API's ASSET_COLUMNS allowlist,
 * which is what seeds filtering) makes that a compile error. Same shape
 * LabAssetColumn uses for the column defs; the array still passes to
 * `filterDefinitions` without a cast since the field is a string subtype.
 */
type LabAssetFilterDef = Omit<FilterDefinition, "dataField"> & {
  // VALIDATION_OVERDUE_FIELD is virtual: LabAssetsTable rewrites it to a
  // next_validation_due comparison (resolveOverdueFilter) before the request.
  dataField:
    | LabAssetSortableColumn
    | typeof VALIDATION_OVERDUE_FIELD
    | `custom_field_${number}`;
};

// Asset Type was dropped in ENG-2122 and restored in ENG-2295. The API ANDs
// the page-level toggle's top-level `asset_type` (LabAssetsSection) with this
// rule, so LabAssetsTable offers it on the All view only and drops the rule
// when the toggle moves to Hardware or Software (dropAssetTypeRules).
export const LabAssetFilterDefs: LabAssetFilterDef[] = [
  {
    dataField: "asset_type",
    label: "Asset Type",
    pluralLabel: "Asset Types",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    selectOptions: ASSET_TYPES.map((value) => ({
      label: LAB_ASSET_TYPE_LABELS[value],
      value,
    })),
  },
  {
    dataField: "asset_number",
    label: "Asset Number",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      StartsWith,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "barcode",
    label: "Barcode",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      StartsWith,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "vendor",
    label: "Vendor",
    pluralLabel: "Vendors",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: metaOptionsQuery("vendors"),
  },
  {
    dataField: "name",
    label: "Name",
    pluralLabel: "Names",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    // Equipment-only: `asset_items.name` is null on software rows.
    query: metaOptionsQuery("names", ["equipment"]),
  },
  {
    dataField: "category",
    label: "Category",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "model",
    label: "Model",
    pluralLabel: "Models",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    // Equipment-only, like the Model column. Software meta omits `models`
    // entirely, so this was already the result — now it's by construction.
    query: metaOptionsQuery("models", ["equipment"]),
  },
  {
    dataField: "serial_number",
    label: "Serial Number",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      StartsWith,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  // Asset dates are varchar YYYY-MM-DD, so day resolution makes Equals and
  // Between plain string comparisons server-side.
  {
    dataField: "purchase_date",
    label: "Purchase Date",
    inputType: "date",
    resolution: "day",
    operators: [
      Operators.Equals,
      Operators.LessThan,
      Operators.GreaterThan,
      Operators.Between,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  // No IsEmpty / IsNotEmpty here: the API resolver's emptiness check is
  // string-shaped (`= ''`, `= '[]'`, `= '{}'`) and MySQL coerces those to 0
  // against the decimal `cost` column, so "is empty" would also match free
  // assets and "is not empty" would hide them. `cost = 0` finds the free ones
  // until the resolver learns column types.
  {
    dataField: "cost",
    label: "Cost",
    inputType: "number",
    operators: [
      Operators.Equals,
      Operators.LessThan,
      Operators.GreaterThan,
      Operators.Between,
    ],
  },
  {
    dataField: "location",
    label: "Location",
    pluralLabel: "Locations",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: metaOptionsQuery("locations"),
  },
  // Software-leaning fields (all columns exist on both asset types).
  {
    dataField: "edition",
    label: "Edition",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "license_name",
    label: "License Name",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "license_key",
    label: "License Key",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "dongle_sn",
    label: "Dongle S/N",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "expire_date",
    label: "Expiration Date",
    inputType: "date",
    resolution: "day",
    operators: [
      Operators.Equals,
      Operators.LessThan,
      Operators.GreaterThan,
      Operators.Between,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "next_validation_due",
    label: "Next Validation Due",
    inputType: "date",
    resolution: "day",
    operators: [
      Operators.Equals,
      Operators.LessThan,
      Operators.GreaterThan,
      Operators.Between,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    // One-click preset over next_validation_due (ENG-1680). "No" resolves to
    // `>= today`, which excludes assets with no due date at all, so the label
    // says so; "Next Validation Due is empty" finds those.
    dataField: VALIDATION_OVERDUE_FIELD,
    label: "Validation overdue",
    pluralLabel: "Validation overdue",
    inputType: "multiselect",
    operators: [Operators.In],
    selectOptions: [
      { label: "Yes", value: "1" },
      { label: "No (has a due date)", value: "0" },
    ],
  },
  {
    dataField: "sms",
    label: "SMS",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "description",
    label: "Description",
    inputType: "text",
    operators: [Operators.Contains, Operators.IsEmpty, Operators.IsNotEmpty],
  },
];

/**
 * The static defs plus one per asset custom field. The API passes
 * `custom_field_<id>` through to its filterResolver, the same resolver the
 * storage routes use, so storage's editor-type → operator mapping applies.
 * On a Hardware or Software view, fields scoped to the other type are left
 * out (the All view lists every field).
 */
export const useLabAssetFilterDefs = (
  assetType?: LabAssetType
): LabAssetFilterDef[] => {
  const { data } = CustomFieldsQuery.useGetCustomFields({
    category: CUSTOM_FIELD_CATEGORIES.ASSET,
  });

  return useMemo(
    () => [
      ...LabAssetFilterDefs,
      ...(data?.data ?? [])
        .filter(
          (field) =>
            !assetType ||
            isCustomFieldApplicableToType(field, ASSET_TYPE_IDS[assetType])
        )
        .map(resolveCustomFieldFilter),
    ],
    [data, assetType]
  );
};
